import { cva, type VariantProps } from "class-variance-authority";
import type { LucideProps } from "lucide-react";

import { lucideRegistry } from "./icon.registry";

import { cn } from "#core/cn";

function BrandGoogleIcon({ className, ...props }: LucideProps) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			{...props}
		>
			<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
			<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
			<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
			<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
		</svg>
	);
}

function BrandMicrosoftIcon({ className, ...props }: LucideProps) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			{...props}
		>
			<path d="M11.4 11.4H2V2h9.4v9.4z" />
			<path d="M22 11.4h-9.4V2H22v9.4z" />
			<path d="M11.4 22H2v-9.4h9.4V22z" />
			<path d="M22 22h-9.4v-9.4H22V22z" />
		</svg>
	);
}

function BrandWordIcon({ className, ...props }: LucideProps) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className={className} {...props}>
			<defs>
				<linearGradient
					id="brand-word-gradient"
					x1="4.494"
					y1="7.914"
					x2="13.832"
					y2="24.086"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#2368c4" />
					<stop offset=".5" stopColor="#1a5dbe" />
					<stop offset="1" stopColor="#1146ac" />
				</linearGradient>
			</defs>
			<path
				d="M28.806,3H9.705A1.192,1.192,0,0,0,8.512,4.191h0V9.5l11.069,3.25L30,9.5V4.191A1.192,1.192,0,0,0,28.806,3Z"
				fill="#41a5ee"
			/>
			<path d="M30,9.5H8.512V16l11.069,1.95L30,16Z" fill="#2b7cd3" />
			<path d="M8.512,16v6.5L18.93,23.8,30,22.5V16Z" fill="#185abd" />
			<path
				d="M9.705,29h19.1A1.192,1.192,0,0,0,30,27.809h0V22.5H8.512v5.309A1.192,1.192,0,0,0,9.705,29Z"
				fill="#103f91"
			/>
			<path
				d="M16.434,8.2H8.512V24.45h7.922a1.2,1.2,0,0,0,1.194-1.191V9.391A1.2,1.2,0,0,0,16.434,8.2Z"
				opacity=".1"
			/>
			<path
				d="M15.783,8.85H8.512V25.1h7.271a1.2,1.2,0,0,0,1.194-1.191V10.041A1.2,1.2,0,0,0,15.783,8.85Z"
				opacity=".2"
			/>
			<path
				d="M15.783,8.85H8.512V23.8h7.271a1.2,1.2,0,0,0,1.194-1.191V10.041A1.2,1.2,0,0,0,15.783,8.85Z"
				opacity=".2"
			/>
			<path
				d="M15.132,8.85H8.512V23.8h6.62a1.2,1.2,0,0,0,1.194-1.191V10.041A1.2,1.2,0,0,0,15.132,8.85Z"
				opacity=".2"
			/>
			<path
				d="M3.194,8.85H15.132a1.193,1.193,0,0,1,1.194,1.191V21.959a1.193,1.193,0,0,1-1.194,1.191H3.194A1.192,1.192,0,0,1,2,21.959V10.041A1.192,1.192,0,0,1,3.194,8.85Z"
				fill="url(#brand-word-gradient)"
			/>
			<path
				d="M6.9,17.988c.023.184.039.344.046.481h.028c.01-.13.032-.287.065-.47s.062-.338.089-.465l1.255-5.407h1.624l1.3,5.326a7.761,7.761,0,0,1,.162,1h.022a7.6,7.6,0,0,1,.135-.975l1.039-5.358h1.477l-1.824,7.748H10.591L9.354,14.742q-.054-.222-.122-.578t-.084-.52H9.127q-.021.189-.084.561c-.042.249-.075.432-.1.552L7.78,19.871H6.024L4.19,12.127h1.5l1.131,5.418A4.469,4.469,0,0,1,6.9,17.988Z"
				fill="#fff"
			/>
		</svg>
	);
}

// The brand marks are not in lucide, so they join the map here.
const iconRegistry = {
	...lucideRegistry,
	"brand-google": BrandGoogleIcon,
	"brand-microsoft": BrandMicrosoftIcon,
	"brand-word": BrandWordIcon,
} as const;

export type IconName = keyof typeof iconRegistry;

const iconVariants = cva("shrink-0", {
	variants: {
		size: {
			"2xs": "size-2",
			xs: "size-3",
			sm: "size-3.5",
			md: "size-4",
			lg: "size-5",
			xl: "size-6",
			"2xl": "size-8",
			"3xl": "size-10",
			"4xl": "size-16",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type IconProps = Omit<LucideProps, "ref" | "size"> &
	VariantProps<typeof iconVariants> & {
		name: IconName;
	};

function Icon({ name, size, className, ...props }: IconProps) {
	const LucideComponent = iconRegistry[name];
	return (
		<LucideComponent
			data-slot="icon"
			className={cn(iconVariants({ size }), className)}
			{...props}
		/>
	);
}

export { Icon, iconRegistry, iconVariants };
export type { IconProps };
